import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Select, Alert, AlertIcon, Tooltip,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiZap, FiSend, FiCalendar, FiAlertTriangle, FiClock, FiMail, FiUsers, FiCreditCard, FiTarget,
  FiCheckCircle, FiSettings,
} from 'react-icons/fi';

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  };
  const instanceId = localStorage.getItem('instance_id');
  if (instanceId) headers['X-Tenant-ID'] = instanceId;
  return headers;
};

const apiFetch = (url) =>
  fetch(url, { headers: getAuthHeaders() }).then((r) => r.json());

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Lightweight "in 3 hours" / "2 hours ago" without pulling in a date library.
const fromNow = (iso) => {
  if (!iso) return '';
  const diffMs = new Date(iso).getTime() - Date.now();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  let out;
  if (mins < 60) out = `${mins}m`;
  else if (mins < 1440) out = `${Math.round(mins / 60)}h`;
  else out = `${Math.round(mins / 1440)}d`;
  return past ? `${out} ago` : `in ${out}`;
};

function StatCard({ icon, label, value, color = 'orange' }) {
  return (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
      <HStack spacing={3} mb={2}>
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
        <Text fontSize="sm" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.05em">
          {label}
        </Text>
      </HStack>
      <Text fontSize="3xl" fontWeight="bold" color={`${color}.600`}>{value}</Text>
    </Box>
  );
}

const TimeCell = ({ iso, overdue }) => (
  <VStack align="start" spacing={0}>
    <Text fontSize="sm" fontWeight="medium" color={overdue ? 'red.500' : undefined}>
      {formatDateTime(iso)}
    </Text>
    <Text fontSize="xs" color={overdue ? 'red.400' : 'gray.500'}>{fromNow(iso)}</Text>
  </VStack>
);

const OverdueBadge = ({ overdue }) =>
  overdue ? <Badge colorScheme="red" fontSize="xs">Overdue</Badge> : null;

// Distinct from OverdueBadge on purpose: a blocked_reason means the dispatch
// loop is running fine but has nothing sendable — no next_run will ever fix
// it, only editing the misconfigured record will.
const MisconfiguredBadge = ({ reason }) => (
  <Tooltip label={reason} placement="top" hasArrow>
    <Badge colorScheme="purple" fontSize="xs">Misconfigured</Badge>
  </Tooltip>
);

const SectionTable = ({ icon, color, title, count, emptyText, children }) => (
  <Box bg="white" borderRadius="lg" boxShadow="md" p={6} mb={6}>
    <HStack mb={4} spacing={2}>
      <Icon as={icon} color={`${color}.500`} />
      <Heading size="md">{title}</Heading>
      <Badge colorScheme={color}>{count}</Badge>
    </HStack>
    {count === 0 ? (
      <Text color="gray.400" fontSize="sm">{emptyText}</Text>
    ) : (
      <TableContainer>{children}</TableContainer>
    )}
  </Box>
);

export default function QueuedCommunications() {
  const [hours, setHours] = useState(24);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch(`/api/v1/queued_communications?hours=${hours}`)
      .then((res) => {
        if (!res.success) {
          setError(typeof res.data === 'string' ? res.data : 'Could not load the communications queue.');
          setPayload(null);
        } else {
          setPayload(res.data);
        }
        setLastRefreshed(new Date());
      })
      .catch(() => setError('Could not load the communications queue.'))
      .finally(() => setLoading(false));
  }, [hours]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 60s so the queue stays current while this page is open.
  useEffect(() => {
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const automations = payload?.automations ?? [];
  const broadcasts = payload?.broadcasts ?? [];
  const eventReminders = payload?.event_reminders ?? [];
  const automationExecutions = payload?.automation_executions ?? [];
  const waitlistNotifications = payload?.waitlist_notifications ?? [];
  const membershipRenewals = payload?.membership_renewals ?? [];
  const targetAlertDigests = payload?.target_alert_digests ?? [];
  const recentlySent = payload?.recently_sent ?? [];
  const counts = payload?.counts ?? {
    automations: 0, broadcasts: 0, event_reminders: 0, automation_executions: 0,
    waitlist_notifications: 0, membership_renewals: 0, target_alert_digests: 0, recently_sent: 0, total: 0,
  };
  const overdueCount = [
    ...automations, ...broadcasts, ...eventReminders,
    ...automationExecutions, ...waitlistNotifications, ...membershipRenewals, ...targetAlertDigests,
  ].filter((i) => i.overdue && !i.blocked_reason).length;
  const misconfiguredCount = counts.event_reminders_misconfigured
    ?? eventReminders.filter((i) => i.blocked_reason).length;

  return (
    <Box>
      <HStack justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Communications Queue</Heading>
          <Text color="gray.500">
            Everything due to run — and everything already sent — across every polling-based dispatch job, across every instance
            {lastRefreshed && ` — refreshed ${lastRefreshed.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Select value={hours} onChange={(e) => setHours(Number(e.target.value))} size="sm" w="200px" bg="white" borderRadius="md">
            <option value={24}>Next 24 hours</option>
            <option value={48}>Next 48 hours</option>
            <option value={72}>Next 72 hours</option>
            <option value={168}>Next 7 days</option>
          </Select>
          <Button leftIcon={<FiRefreshCw />} colorScheme="orange" size="sm" onClick={fetchData} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      {error ? (
        <Alert status="warning" borderRadius="lg"><AlertIcon />{error}</Alert>
      ) : loading && !payload ? (
        <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={5} mb={8}>
            <StatCard icon={FiClock} label="Total Queued" value={counts.total} color="orange" />
            <StatCard icon={FiZap} label="Automations" value={counts.automations} color="purple" />
            <StatCard icon={FiSend} label="Broadcasts" value={counts.broadcasts} color="teal" />
            <StatCard
              icon={FiAlertTriangle}
              label="Overdue"
              value={overdueCount}
              color={overdueCount > 0 ? 'red' : 'gray'}
            />
            <StatCard
              icon={FiSettings}
              label="Misconfigured"
              value={misconfiguredCount}
              color={misconfiguredCount > 0 ? 'purple' : 'gray'}
            />
          </SimpleGrid>
          {misconfiguredCount > 0 && (
            <Alert status="info" borderRadius="lg" mb={6}>
              <AlertIcon />
              {misconfiguredCount} event reminder{misconfiguredCount === 1 ? '' : 's'} can't be sent because the
              event (or its parent) has no email template text — these are not stuck on a dispatch loop and won't
              clear until the template is set. See the "Misconfigured" badges in Event Reminders below.
            </Alert>
          )}

          <SectionTable icon={FiCheckCircle} color="green" title="Recently Sent" count={recentlySent.length}
            emptyText="Nothing sent in this window yet">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Subject</Th>
                  <Th>Receiver</Th>
                  <Th>Type</Th>
                  <Th>Sent at</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentlySent.map((item) => (
                  <Tr key={item.email_id}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.subject}</Td>
                    <Td>{item.receiver}</Td>
                    <Td>{item.is_broadcast ? <Badge colorScheme="teal" fontSize="xs">Broadcast</Badge> : null}</Td>
                    <Td><TimeCell iso={item.sent_on} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiZap} color="purple" title="Automations" count={automations.length}
            emptyText="No automation rules due in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Rule</Th>
                  <Th>Trigger</Th>
                  <Th>Template</Th>
                  <Th>Next run</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {automations.map((item) => (
                  <Tr key={item.rule_id}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.rule_name}</Td>
                    <Td>{item.trigger_type}{item.schedule_type ? ` · ${item.schedule_type}` : ''}</Td>
                    <Td>{item.email_template || '—'}</Td>
                    <Td><TimeCell iso={item.next_run} overdue={item.overdue} /></Td>
                    <Td><OverdueBadge overdue={item.overdue} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiSend} color="teal" title="Broadcasts" count={broadcasts.length}
            emptyText="No scheduled broadcasts due in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Broadcast</Th>
                  <Th>Subject</Th>
                  <Th isNumeric>Recipients</Th>
                  <Th>Scheduled at</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {broadcasts.map((item) => (
                  <Tr key={item.broadcast_id}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.name}</Td>
                    <Td>{item.subject}</Td>
                    <Td isNumeric>{item.recipient_count ?? '—'}</Td>
                    <Td><TimeCell iso={item.scheduled_at} overdue={item.overdue} /></Td>
                    <Td><OverdueBadge overdue={item.overdue} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiCalendar} color="orange" title="Event Reminders" count={eventReminders.length}
            emptyText="No event reminders due in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Event</Th>
                  <Th>Reminder</Th>
                  <Th>Event start</Th>
                  <Th>Trigger at</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {eventReminders.map((item, idx) => (
                  <Tr key={`${item.event_instance_id}-${item.reminder_type}-${idx}`}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.event_instance_name}</Td>
                    <Td>{item.reminder_type}</Td>
                    <Td>{formatDateTime(item.event_start)}</Td>
                    <Td><TimeCell iso={item.trigger_at} overdue={item.overdue} /></Td>
                    <Td>
                      {item.blocked_reason
                        ? <MisconfiguredBadge reason={item.blocked_reason} />
                        : <OverdueBadge overdue={item.overdue} />}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiMail} color="cyan" title="Automation Executions" count={automationExecutions.length}
            emptyText="No pending per-recipient automation sends in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Rule</Th>
                  <Th>Recipient</Th>
                  <Th>Scheduled for</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {automationExecutions.map((item) => (
                  <Tr key={item.execution_id}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.rule_name}</Td>
                    <Td>{item.recipient_email}</Td>
                    <Td>
                      {item.scheduled_for ? (
                        <TimeCell iso={item.scheduled_for} overdue={item.overdue} />
                      ) : (
                        <Badge colorScheme="red" fontSize="xs">Immediate</Badge>
                      )}
                    </Td>
                    <Td><OverdueBadge overdue={item.overdue} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiUsers} color="green" title="Waitlist Notifications" count={waitlistNotifications.length}
            emptyText="No waitlist promotions due in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Event</Th>
                  <Th>Profile</Th>
                  <Th isNumeric>Priority</Th>
                  <Th>Scheduled time</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {waitlistNotifications.map((item) => (
                  <Tr key={item.notification_id}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.event_instance_name}</Td>
                    <Td>{item.profile_email || '—'}</Td>
                    <Td isNumeric>{item.priority}</Td>
                    <Td><TimeCell iso={item.scheduled_time} overdue={item.overdue} /></Td>
                    <Td><OverdueBadge overdue={item.overdue} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiCreditCard} color="pink" title="Membership Renewals" count={membershipRenewals.length}
            emptyText="No membership renewal or expiry reminders due in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Profile</Th>
                  <Th>Membership</Th>
                  <Th>Reminder</Th>
                  <Th>End date</Th>
                  <Th>Trigger at</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {membershipRenewals.map((item, idx) => (
                  <Tr key={`${item.membership_id}-${item.reminder_type}-${idx}`}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.profile_email}</Td>
                    <Td>{item.membership_name}</Td>
                    <Td>{item.reminder_type}</Td>
                    <Td>{formatDateTime(item.end_date)}</Td>
                    <Td><TimeCell iso={item.trigger_at} overdue={item.overdue} /></Td>
                    <Td><OverdueBadge overdue={item.overdue} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>

          <SectionTable icon={FiTarget} color="cyan" title="Target Alert Digests" count={targetAlertDigests.length}
            emptyText="No daily/weekly target alert digests due in this window">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Instance</Th>
                  <Th>Profile</Th>
                  <Th>Industry</Th>
                  <Th>Category</Th>
                  <Th>Frequency</Th>
                  <Th>Trigger at</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {targetAlertDigests.map((item) => (
                  <Tr key={item.alert_id}>
                    <Td>{item.instance_name}</Td>
                    <Td fontWeight="medium">{item.profile_email}</Td>
                    <Td>{item.industry}</Td>
                    <Td>{item.category}</Td>
                    <Td>{item.frequency}</Td>
                    <Td><TimeCell iso={item.trigger_at} overdue={item.overdue} /></Td>
                    <Td><OverdueBadge overdue={item.overdue} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </SectionTable>
        </>
      )}
    </Box>
  );
}
